import { useState, useEffect } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import AuthLayout from "../components/auth/AuthLayout";
import { resetPassword } from "../api/auth";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Strictly enforce no-referrer policy to prevent leaking reset token to external origins
    let meta = document.querySelector('meta[name="referrer"]') as HTMLMetaElement | null;
    let created = false;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "referrer";
      meta.content = "no-referrer";
      document.head.appendChild(meta);
      created = true;
    } else {
      meta.content = "no-referrer";
    }

    if (!token) {
      setError("Password reset token is missing or malformed. Please request a new reset link.");
    }

    return () => {
      if (created && meta?.parentNode) {
        meta.parentNode.removeChild(meta);
      }
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError("Invalid or missing reset token.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await resetPassword({
        token,
        new_password: password,
      });

      setSuccess(true);
      setTimeout(() => {
        navigate("/login", {
          state: { message: "Password has been successfully updated! You can now sign in." },
        });
      }, 1500);
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: {
          data?: {
            detail?: string;
          };
        };
        message?: string;
      };
      setError(axiosErr.response?.data?.detail || "Password reset failed. The link may have expired or already been used.");
    } finally {
      setLoading(false);
    }
  };

  if (import.meta.env.VITE_LAUNCH_MODE === "true" || import.meta.env.VITE_ACCESS_REQUEST_MODE === "true") {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthLayout>
      <h2 className="tx-auth-form-title">Create new password</h2>
      <p className="tx-auth-form-subtitle">
        Your new password must be at least 8 characters long and different from previously used passwords.
      </p>

      {success && (
        <div className="tx-auth-alert tx-auth-alert-success" role="status">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <div>
            <p style={{ fontWeight: 600, margin: 0 }}>Password updated successfully!</p>
            <p style={{ margin: "4px 0 0 0", fontSize: 13 }}>Redirecting you to sign in...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="tx-auth-alert tx-auth-alert-error" role="alert">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {!token ? (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <Link to="/forgot-password" className="tx-auth-btn-primary" style={{ textDecoration: "none" }}>
            Request New Reset Link
          </Link>
        </div>
      ) : (
        <form className="tx-auth-form" onSubmit={handleSubmit} noValidate>
          <div className="tx-auth-field">
            <label className="tx-auth-label" htmlFor="tx-new-password">New Password</label>
            <div className="tx-auth-input-wrapper">
              <input
                id="tx-new-password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                required
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="tx-auth-input"
                disabled={loading || success}
              />
              <button
                type="button"
                className="tx-auth-eye-btn"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="tx-auth-field">
            <label className="tx-auth-label" htmlFor="tx-confirm-new-password">Confirm New Password</label>
            <input
              id="tx-confirm-new-password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="tx-auth-input"
              disabled={loading || success}
            />
          </div>

          <button
            type="submit"
            className="tx-auth-btn-primary"
            disabled={loading || success}
            id="btn-reset-password"
          >
            {loading ? (
              <>
                <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
                <span>Updating Password...</span>
              </>
            ) : (
              "Update Password"
            )}
          </button>

          <div className="tx-auth-card-footer">
            <Link to="/login" className="tx-auth-link">
              Return to Sign in
            </Link>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
